<template>
  <div class="price-cell" :class="stateClass">
    <input
      type="number"
      min="0"
      step="0.01"
      class="custom-form-control price-input"
      :class="{ empty: isEmpty, sprinter: sprinter }"
      :value="display"
      :placeholder="placeholder"
      :disabled="saving"
      @focus="onFocus"
      @blur="onBlur"
      @input="local = $event.target.value"
    />
  </div>
</template>

<script>
import { BasePriceService } from '@/core/services'

export default {
  name: 'PriceCell',
  props: {
    districtId: { type: String, required: true },
    vehicleType: { type: String, required: true },
    routeType: { type: String, required: true },
    value: { type: Number, default: null }, // precio actual o null
    sprinter: { type: Boolean, default: false }
  },
  data() {
    return {
      local: this.value != null ? String(this.value) : '',
      state: 'idle', // idle | ok | error
      saving: false,
      focused: false
    }
  },
  computed: {
    isEmpty() { return this.value == null },
    // Placeholder "—": rojo si falta precio (llama la atención), gris para Sprinter.
    placeholder() { return '—' },
    display() {
      // Mientras edita, muestra lo tecleado; si no, el valor formateado.
      if (this.focused) return this.local
      return this.value != null ? String(this.value) : ''
    },
    stateClass() {
      if (this.state === 'ok') return 'flash-ok'
      if (this.state === 'error') return 'flash-error'
      return ''
    }
  },
  watch: {
    value(v) { this.local = v != null ? String(v) : '' }
  },
  methods: {
    onFocus() {
      this.focused = true
      this.local = this.value != null ? String(this.value) : ''
    },
    async onBlur() {
      this.focused = false
      const raw = (this.local || '').trim()
      const num = raw === '' ? 0 : parseFloat(raw)

      // Validación mínima: negativo o NaN → revertir sin guardar.
      if (isNaN(num) || num < 0) {
        this.local = this.value != null ? String(this.value) : ''
        return
      }
      // Borrado: vacío/0 cuando no había precio → nada que hacer.
      if (num === 0 && this.value == null) return
      // Sin cambio.
      if (num !== 0 && num === this.value) return

      await this.save(num)
    },
    async save(amount) {
      this.saving = true
      try {
        await BasePriceService.updateBasePrices([{
          district_id: this.districtId,
          vehicle_type: this.vehicleType,
          route_type: this.routeType,
          base_amount: amount // 0 = borrar
        }])
        this.flash('ok')
        // amount 0 → precio eliminado (null); si no, el nuevo valor.
        this.$emit('saved', {
          districtId: this.districtId,
          vehicleType: this.vehicleType,
          amount: amount === 0 ? null : amount
        })
      } catch (e) {
        console.error(e)
        this.flash('error')
        this.local = this.value != null ? String(this.value) : '' // revertir
      } finally {
        this.saving = false
      }
    },
    flash(kind) {
      this.state = kind
      if (this._t) clearTimeout(this._t)
      this._t = setTimeout(() => { this.state = 'idle' }, 1000)
    }
  },
  beforeDestroy() {
    if (this._t) clearTimeout(this._t)
  }
}
</script>

<style lang="stylus" scoped>
.price-cell
  border-radius 6px
  transition background-color .3s ease
// El tamaño y el borde los pone custom-form-control (wize-admin); aquí solo
// lo que la rejilla de precios necesita.
.price-input
  min-width 80px
  text-align right
  background-color #fff
  &.empty::placeholder
    color #dc3545          // "—" rojo: falta precio
    opacity 1
  &.sprinter.empty::placeholder
    color #adb5bd          // "—" gris: Sprinter rara vez tiene tarifa
.flash-ok
  background-color rgba(37,194,110,.25)
.flash-error
  background-color rgba(220,53,69,.25)
</style>
